import {QueryContractInfoResponse} from "@shareledgerjs/types/cosmwasm/wasm/v1/query";

import {BaseClient} from "../../baseclient";
import {WasmQueryExtension} from "../wasm";
import {
  AllAccountsResponse,
  AllAllowancesResponse,
  AllowanceInfo,
  AllowanceResponse,
  AllSpenderAllowancesResponse,
  BalanceResponse,
  MinterResponse,
  QueryMsg,
  SpenderAllowanceInfo,
  TokenInfoResponse
} from "./types/Cw20Base.types";

export interface TokenQueryExtensionMethods {
  balance(contractAddress: string, address: string, height?: number): Promise<string>;
  minter(contractAddress: string, height?: number): Promise<MinterResponse>;
  holders(contractAddress: string, after?: string, limit?: number, height?: number): Promise<string[]>;
  allowance(contractAddress: string, owner: string, spender: string, height?: number): Promise<AllowanceResponse>;
  allowances(contractAddress: string, owner: string, after?: string, limit?: number, height?: number): Promise<AllowanceInfo[]>;
  spenderAllowances(
    contractAddress: string,
    spender: string,
    after?: string,
    limit?: number,
    height?: number
  ): Promise<SpenderAllowanceInfo[]>;
  tokenMetadata(contractAddress: string, height?: number): Promise<TokenInfoResponse>;
  metadata(contractAddress: string, height?: number): Promise<QueryContractInfoResponse>;
}

export interface TokenQueryExtension {
  readonly token: TokenQueryExtensionMethods;
}

export function TokenQueryExtension<T extends {new (...args: any[]): BaseClient & TokenQueryExtension & WasmQueryExtension}>(
  constructor: T
): T {
  return class extends constructor {
    override get token() {
      return {
        ...super["token"],
        balance: async (contractAddress: string, address: string) => {
          const res = await this.wasm.smartContractState<BalanceResponse, QueryMsg>(contractAddress, {
            balance: {
              address
            }
          });
          return res.balance;
        },
        minter: async (contractAddress: string) => {
          const res = await this.wasm.smartContractState<MinterResponse, QueryMsg>(contractAddress, {
            minter: {}
          });
          return res;
        },
        holders: async (contractAddress: string, after?: string, limit = 10) => {
          const res = await this.wasm.smartContractState<AllAccountsResponse, QueryMsg>(contractAddress, {
            all_accounts: {
              limit,
              start_after: after
            }
          });
          return res.accounts;
        },
        allowance: async (contractAddress: string, owner: string, spender: string) => {
          const res = await this.wasm.smartContractState<AllowanceResponse, QueryMsg>(contractAddress, {
            allowance: {
              spender,
              owner
            }
          });
          return res;
        },
        allowances: async (contractAddress: string, owner: string, after?: string, limit = 10) => {
          const res = await this.wasm.smartContractState<AllAllowancesResponse, QueryMsg>(contractAddress, {
            all_allowances: {
              owner,
              limit,
              start_after: after
            }
          });
          return res.allowances;
        },
        spenderAllowances: async (contractAddress: string, spender: string, after?: string, limit = 10) => {
          const res = await this.wasm.smartContractState<AllSpenderAllowancesResponse, QueryMsg>(contractAddress, {
            all_spender_allowances: {
              spender,
              limit,
              start_after: after
            }
          });
          return res.allowances;
        },
        tokenMetadata: async (contractAddress: string) => {
          const res = await this.wasm.smartContractState<TokenInfoResponse, QueryMsg>(contractAddress, {
            token_info: {}
          });
          return res;
        },
        metadata: async (contractAddress: string) => {
          const res = await this.wasm.contractInfo(contractAddress);
          return res;
        }
      };
    }
  };
}
